// https://ui.shadcn.com/docs/components/table#usage
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/utils/tw-helpers'

const tableContainerVariants = cva('w-full overflow-hidden', {
  variants: {
    variant: {
      default: 'border border-[#C4C4C4] rounded',
      'border-row': '',
      enterprise: '',
    },
  },
})

interface ITable extends VariantProps<typeof tableContainerVariants> {}

const TableContext = React.createContext<ITable>({
  variant: 'default',
})

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  variant?: ITable['variant']
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, variant = 'default', ...props }, ref) => {
    const tableContextValue = React.useMemo(() => ({ variant }), [variant])

    return (
      <div className={cn(tableContainerVariants({ variant, className }))}>
        <TableContext.Provider value={tableContextValue}>
          <table
            ref={ref}
            className="w-full caption-bottom text-sm"
            {...props}
          />
        </TableContext.Provider>
      </div>
    )
  }
)
Table.displayName = 'Table'

const tableHeaderVariants = cva('', {
  variants: {
    variant: {
      default:
        '[&_tr]:border-b [&_th:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r-[#C4C4C4]',
      'border-row': '',
      enterprise: '',
    },
  },
})

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TableContext)

  return (
    <thead
      ref={ref}
      className={cn(tableHeaderVariants({ variant, className }))}
      {...props}
    />
  )
})
TableHeader.displayName = 'TableHeader'

const tableBodyVariants = cva('', {
  variants: {
    variant: {
      default:
        '[&_tr:last-child]:border-0 [&_td:not(:last-child)]:border-r [&_td:not(:last-child)]:border-r-[#C4C4C4]',
      'border-row': '',
      enterprise: '',
    },
  },
})

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TableContext)

  return (
    <tbody
      ref={ref}
      className={cn(tableBodyVariants({ variant, className }))}
      {...props}
    />
  )
})
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('bg-primary text-primary-foreground font-medium', className)}
    {...props}
  />
))
TableFooter.displayName = 'TableFooter'

const TableRowVariants = cva('', {
  variants: {
    variant: {
      default:
        'hover:bg-muted/50 data-[state=selected]:bg-muted border-b border-b-gray-400 transition-colors',
      'border-row': '',
      enterprise: 'border-b border-outline',
    },
  },
})

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TableContext)

  return (
    <tr
      ref={ref}
      className={cn(TableRowVariants({ variant, className }))}
      {...props}
    />
  )
})
TableRow.displayName = 'TableRow'

const tableHeadVariants = cva(
  'h-9 px-4 text-left text-sm align-middle font-bold text-muted-foreground [&:has([role=checkbox])]:pr-0',
  {
    variants: {
      variant: {
        default: 'bg-[#F9F9F9]',
        'border-row': '',
        enterprise: 'text-[13px] font-bold text-[#828488] h-[37px] p-0',
      },
    },
  }
)

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TableContext)

  return (
    <th
      ref={ref}
      className={cn(tableHeadVariants({ variant, className }))}
      {...props}
    />
  )
})
TableHead.displayName = 'TableHead'

const TableCellVariants = cva(
  'p-[13px] align-middle [&:has([role=checkbox])]:pr-0',
  {
    variants: {
      variant: {
        default: '',
        'border-row': '',
        enterprise: 'p-0 h-[72px] text-[15px] text-text font-bold',
      },
    },
  }
)

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TableContext)

  return (
    <td
      ref={ref}
      className={cn(TableCellVariants({ variant, className }))}
      {...props}
    />
  )
})
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('text-muted-foreground mt-4 text-sm', className)}
    {...props}
  />
))
TableCaption.displayName = 'TableCaption'

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
}
